/* ============================================================
   NutraHarmony Berberine Blend — shared funnel interactions
   Accordion (smooth CSS-grid height) · Swiper init · sticky CTA ·
   smooth-scroll · offer selector.
   ============================================================ */
(function () {
  'use strict';

  var swiperLoading = false;
  var swiperQueue = [];

  function findAll(root, selector) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) nodes.unshift(scope);
    return nodes;
  }

  /* ---- Accordion (single-open per .brb_faq group; .brb_faq--multi allows many open) ---- */
  function setFaqOpen(item, open) {
    if (!item) return;
    item.classList.toggle('brb_is_open', !!open);
    var head = item.querySelector('.brb_faq__head');
    if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function initAccordion(root) {
    findAll(root, '.brb_faq--first-open').forEach(function (group) {
      if (!group.querySelector('.brb_faq__item.brb_is_open')) {
        setFaqOpen(group.querySelector('.brb_faq__item'), true);
      }
    });

    findAll(root, '.brb_faq__head').forEach(function (head) {
      if (head.__nhBound) return;
      head.__nhBound = true;
      var initialItem = head.closest('.brb_faq__item');
      if (initialItem && initialItem.classList.contains('brb_is_open')) head.setAttribute('aria-expanded', 'true');
      head.addEventListener('click', function () {
        var item = head.closest('.brb_faq__item');
        var group = head.closest('.brb_faq');
        if (!item) return;
        var open = item.classList.contains('brb_is_open');
        if (group && !group.classList.contains('brb_faq--multi')) {
          group.querySelectorAll('.brb_faq__item.brb_is_open').forEach(function (i) {
            if (i !== item) setFaqOpen(i, false);
          });
        }
        setFaqOpen(item, !open);
      });
    });
  }

  /* ---- Swiper (loads from CDN, data-driven config) ----
     Supported data-* on a .brb_swiper element:
       data-per-view   number | "auto"     (default 1)
       data-gap        px spaceBetween      (default 0)
       data-mobile-gap px spaceBetween below 768px
       data-offset-after px space after the final slide
       data-loop       "true" to loop
       data-autoplay   delay ms (presence enables autoplay)
       data-speed      transition ms        (default 500)
       data-effect     "fade"
       data-direction  "vertical"
       data-thumb-strip  marks a thumbnail strip (free-mode, watch progress)
       data-thumbs     CSS selector of the thumb strip this main slider syncs to
       data-cycle-loop "true" for pre-rendered [copy][real][copy] slides with
                       silent rebasing after edges; avoids native loop jumps
                       on centered auto-width sliders with very few slides
       data-mobile-only "true" initializes only below 768px
     Nav arrows: place .swiper-button-prev/next as siblings (in the parent).  */
  function initSwipers(root) {
    var nodes = findAll(root, '.brb_swiper');
    if (!nodes.length) return;
    function num(el, attr, def) {
      var v = el.getAttribute(attr);
      if (v === null || v === '') return def;
      if (v === 'auto') return 'auto';
      var n = parseFloat(v);
      return isNaN(n) ? def : n;
    }
    function bindAutoplayPause(el) {
      if (el.__nhAutoplayPauseBound || !el.__swiper || !el.__swiper.autoplay) return;
      el.__nhAutoplayPauseBound = true;
      var swiper = el.__swiper;
      var isHovering = false;
      var hasFocus = false;

      function syncAutoplay() {
        var shouldPause = isHovering || hasFocus;
        el.__nhAutoplayPaused = shouldPause;
        if (swiper.destroyed || !swiper.autoplay) return;
        if (shouldPause) {
          if (swiper.autoplay.stop) swiper.autoplay.stop();
        } else if (swiper.params && swiper.params.autoplay && swiper.autoplay.start) {
          swiper.autoplay.start();
        }
      }

      el.addEventListener('mouseenter', function () {
        isHovering = true;
        syncAutoplay();
      });
      el.addEventListener('mouseleave', function () {
        isHovering = false;
        syncAutoplay();
      });
      el.addEventListener('focusin', function () {
        hasFocus = true;
        syncAutoplay();
      });
      el.addEventListener('focusout', function (event) {
        if (!event.relatedTarget || !el.contains(event.relatedTarget)) {
          hasFocus = false;
          syncAutoplay();
        }
      });
    }
    function bindSlideCounter(el) {
      if (el.__brbQuoteCounterBound || !el.__swiper) return;
      var counter = el.parentElement ? el.parentElement.querySelector('[data-brb-quote-counter]') : null;
      var current = counter ? counter.querySelector('[data-brb-quote-current]') : null;
      var realCount = parseInt(el.getAttribute('data-real-count') || '0', 10) || 0;
      if (!counter || !current || realCount < 2) return;
      el.__brbQuoteCounterBound = true;
      var swiper = el.__swiper;

      function syncCounter() {
        var index = el.getAttribute('data-cycle-loop') === 'true' || typeof swiper.realIndex !== 'number'
          ? swiper.activeIndex
          : swiper.realIndex;
        index %= realCount;
        if (index < 0) index += realCount;
        var number = index + 1;
        current.textContent = String(number);
        counter.setAttribute('aria-label', 'Quote ' + number + ' of ' + realCount);
      }

      swiper.on('slideChange', syncCounter);
      syncCounter();
    }
    function buildOne(el) {
      if (el.__swiper) return;
      if (el.getAttribute('data-mobile-only') === 'true' && window.matchMedia && !window.matchMedia('(max-width: 767px)').matches) return;
      var slideCount = el.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
      if (!slideCount) return;
      var gap = parseInt(el.getAttribute('data-gap') || '0', 10);
      if (!isFinite(gap)) gap = 0;
      var mobileGap = parseInt(el.getAttribute('data-mobile-gap') || '', 10);
      var offsetAfter = parseInt(el.getAttribute('data-offset-after') || '0', 10);
      if (!isFinite(offsetAfter) || offsetAfter < 0) offsetAfter = 0;
      var opts = {
        slidesPerView: num(el, 'data-per-view', 1),
        spaceBetween: gap,
        slidesOffsetAfter: offsetAfter,
        loop: false,
        initialSlide: parseInt(el.getAttribute('data-initial-slide') || '0', 10) || 0,
        speed: parseInt(el.getAttribute('data-speed') || '500', 10),
        observer: true,
        observeParents: true,
        observeSlideChildren: true
      };
      if (el.getAttribute('data-centered') === 'true') opts.centeredSlides = true;
      var requestedLoop = el.getAttribute('data-loop') === 'true';
      var cycleLoop = el.getAttribute('data-cycle-loop') === 'true';
      var loopPad = el.getAttribute('data-loop-pad') === 'true';
      var realCount = parseInt(el.getAttribute('data-real-count') || '0', 10) || 0;
      var perViewForLoop = opts.slidesPerView === 'auto' ? 1 : opts.slidesPerView;
      opts.loop = requestedLoop && !cycleLoop && slideCount > perViewForLoop;
      if (opts.loop && loopPad) opts.loopAdditionalSlides = realCount > 0 ? realCount : slideCount;
      if (opts.loop && opts.centeredSlides && opts.slidesPerView === 'auto') {
        opts.loopAdditionalSlides = loopPad && realCount > 0 ? realCount : 1;
      }
      if (el.hasAttribute('data-autoplay')) {
        opts.autoplay = { delay: parseInt(el.getAttribute('data-autoplay') || '3500', 10), disableOnInteraction: false };
      }
      if (typeof opts.slidesPerView === 'number' && opts.slidesPerView > 1) {
        opts.breakpoints = {
          0: { slidesPerView: 1 },
          640: { slidesPerView: Math.min(2, opts.slidesPerView) },
          1000: { slidesPerView: opts.slidesPerView }
        };
      }
      if (isFinite(mobileGap)) {
        opts.breakpoints = opts.breakpoints || {};
        opts.breakpoints[0] = Object.assign({}, opts.breakpoints[0] || {}, { spaceBetween: mobileGap });
        opts.breakpoints[768] = Object.assign({}, opts.breakpoints[768] || {}, { spaceBetween: gap });
      }
      if (el.getAttribute('data-effect') === 'fade') { opts.effect = 'fade'; opts.fadeEffect = { crossFade: true }; }
      if (el.getAttribute('data-direction') === 'vertical') opts.direction = 'vertical';
      if (el.hasAttribute('data-thumb-strip')) { opts.watchSlidesProgress = true; opts.freeMode = true; opts.slideToClickedSlide = true; }
      if (el.getAttribute('data-click-slide') === 'true') opts.slideToClickedSlide = true;
      if (!el.hasAttribute('data-thumb-strip')) {
        var nextEl = el.querySelector('.swiper-button-next') || (el.parentElement ? el.parentElement.querySelector('.swiper-button-next') : null);
        var prevEl = el.querySelector('.swiper-button-prev') || (el.parentElement ? el.parentElement.querySelector('.swiper-button-prev') : null);
        if (nextEl || prevEl) opts.navigation = { nextEl: nextEl, prevEl: prevEl };
      }
      var pag = el.querySelector('.swiper-pagination');
      if (pag) opts.pagination = { el: pag, clickable: true };
      var thumbSel = el.getAttribute('data-thumbs');
      if (thumbSel) {
        var tEl = document.querySelector(thumbSel);
        if (tEl && el.getAttribute('data-cycle-loop') !== 'true') { buildOne(tEl); if (tEl.__swiper) opts.thumbs = { swiper: tEl.__swiper }; }
      }
      el.__swiper = new Swiper(el, opts);
      bindAutoplayPause(el);
      bindSlideCounter(el);
      bindCycleLoop(el);
    }

    function bindCycleLoop(el) {
      if (el.__nhCycleBound || el.getAttribute('data-cycle-loop') !== 'true' || !el.__swiper) return;
      el.__nhCycleBound = true;
      var swiper = el.__swiper;
      var realCount = parseInt(el.getAttribute('data-real-count') || '0', 10) || 0;
      if (realCount < 2) return;
      var thumbSelector = el.getAttribute('data-cycle-thumbs') || '';
      var thumbEl = thumbSelector ? document.querySelector(thumbSelector) : null;
      if (thumbEl) buildOne(thumbEl);
      var thumbSwiper = thumbEl && thumbEl.__swiper ? thumbEl.__swiper : null;
      var thumbSlides = thumbEl ? Array.prototype.slice.call(thumbEl.querySelectorAll('.swiper-wrapper > .swiper-slide')) : [];
      var gallery = el.closest('.brb_gallery');
      var rebaseTimer = null;
      var rebaseFrame = null;
      var isRebasing = false;

      function realIndex(index) {
        var i = index % realCount;
        return i < 0 ? i + realCount : i;
      }

      function middleIndex(index) {
        return realCount + realIndex(index);
      }

      function setThumb(real) {
        thumbSlides.forEach(function (slide) {
          slide.classList.toggle('swiper-slide-thumb-active', parseInt(slide.getAttribute('data-real-index') || '-1', 10) === real);
        });
        if (thumbSwiper && thumbSwiper.slideTo) thumbSwiper.slideTo(real, 250);
      }

      function syncThumb() {
        var real = realIndex(swiper.activeIndex);
        setThumb(real);
        if (gallery) gallery.classList.toggle('brb_gallery--first-active', real === 0);
      }

      function clearRebaseClass(restartAutoplay) {
        if (rebaseFrame) window.cancelAnimationFrame(rebaseFrame);
        rebaseFrame = window.requestAnimationFrame(function () {
          rebaseFrame = window.requestAnimationFrame(function () {
            el.classList.remove('brb_is_rebasing');
            rebaseFrame = null;
            isRebasing = false;
            if (restartAutoplay && !el.__nhAutoplayPaused && swiper.autoplay && swiper.autoplay.start) swiper.autoplay.start();
          });
        });
      }

      function jumpSilently(index) {
        var target = middleIndex(index);
        if (swiper.activeIndex === target) return;
        var restartAutoplay = !!(swiper.autoplay && swiper.autoplay.running && swiper.autoplay.stop);
        if (restartAutoplay) swiper.autoplay.stop();
        isRebasing = true;
        el.classList.add('brb_is_rebasing');
        if (swiper.setTransition) swiper.setTransition(0);
        swiper.slideTo(target, 0, false);
        if (swiper.updateSlidesClasses) swiper.updateSlidesClasses();
        if (swiper.setTransition) swiper.setTransition(0);
        clearRebaseClass(restartAutoplay);
      }

      function rebaseIfNeeded() {
        if (!swiper || swiper.destroyed) return;
        if (rebaseTimer) {
          window.clearTimeout(rebaseTimer);
          rebaseTimer = null;
        }
        if (swiper.activeIndex < realCount || swiper.activeIndex >= realCount * 2) {
          jumpSilently(swiper.activeIndex);
        }
        syncThumb();
      }

      function scheduleRebase(duration) {
        if (rebaseTimer) window.clearTimeout(rebaseTimer);
        var wait = parseInt(duration, 10);
        if (!isFinite(wait)) wait = swiper.params.speed || 500;
        rebaseTimer = window.setTimeout(rebaseIfNeeded, wait + 40);
      }

      var slideNext = swiper.slideNext.bind(swiper);
      var slidePrev = swiper.slidePrev.bind(swiper);
      swiper.slideNext = function (speed, runCallbacks) {
        if (swiper.activeIndex >= realCount * 2) rebaseIfNeeded();
        var result = slideNext(speed, runCallbacks);
        scheduleRebase(typeof speed === 'number' ? speed : swiper.params.speed);
        return result;
      };
      swiper.slidePrev = function (speed, runCallbacks) {
        if (swiper.activeIndex < realCount) rebaseIfNeeded();
        var result = slidePrev(speed, runCallbacks);
        scheduleRebase(typeof speed === 'number' ? speed : swiper.params.speed);
        return result;
      };

      swiper.on('slideChange', function () {
        syncThumb();
        if (!isRebasing) scheduleRebase();
      });
      swiper.on('slideChangeTransitionEnd', function () {
        if (!isRebasing) rebaseIfNeeded();
      });
      swiper.on('transitionEnd', function () {
        if (!isRebasing) rebaseIfNeeded();
      });

      thumbSlides.forEach(function (slide, index) {
        if (slide.__nhCycleThumb) return;
        slide.__nhCycleThumb = true;
        slide.addEventListener('click', function () {
          swiper.slideTo(realCount + index, swiper.params.speed || 500);
        });
      });

      if (swiper.activeIndex < realCount || swiper.activeIndex >= realCount * 2) {
        jumpSilently(swiper.activeIndex);
      }
      window.setTimeout(rebaseIfNeeded, 0);
      syncThumb();
    }
    function build() { nodes.forEach(buildOne); }
    if (typeof Swiper === 'undefined') {
      if (!document.querySelector('link[rel="stylesheet"][href*="swiper-bundle"]')) {
        var l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
        document.head.appendChild(l);
      }
      swiperQueue.push(build);
      if (swiperLoading) return;
      swiperLoading = true;
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      s.onload = function () {
        var queue = swiperQueue.slice();
        swiperLoading = false;
        queue.forEach(function (fn) { fn(); });
        swiperQueue = [];
      };
      s.onerror = function () {
        swiperLoading = false;
        swiperQueue = [];
      };
      document.head.appendChild(s);
    } else {
      build();
    }
  }

  function destroySwipers(root) {
    findAll(root, '.brb_swiper').forEach(function (el) {
      if (el.__swiper && el.__swiper.destroy) {
        el.__swiper.destroy(true, true);
        el.__swiper = null;
      }
    });
  }

  /* ---- /of hero image gallery (self-contained: no Swiper) ----
     Stacked slides + prev/next arrows (hidden at first/last) + thumbnail strip.
     Active thumb gets the .brb_is_active class; arrows toggle .brb_is_hidden. ---- */
  function initHofGallery(root) {
    findAll(root, '[data-hof-gallery]').forEach(function (g) {
      if (g.__nhHof) return;
      g.__nhHof = true;
      var slides = [].slice.call(g.querySelectorAll('[data-hof-slide]'));
      var thumbs = [].slice.call(g.querySelectorAll('[data-hof-thumb]'));
      var prev = g.querySelector('[data-hof-prev]');
      var next = g.querySelector('[data-hof-next]');
      var track = g.querySelector('[data-hof-track]');
      var viewport = g.querySelector('.brb_hof__viewport');
      var thumbsEl = g.querySelector('.brb_hof__thumbs');
      var thumbtrack = g.querySelector('[data-hof-thumbtrack]');
      var n = slides.length;
      if (!n || !track) return;
      var i = 0;
      var thumbStart = 0;
      // translate the thumb strip in WHOLE-thumb steps so the active thumb stays
      // in the clipped window and the edge thumb is never cut to a partial sliver.
      function layoutThumbs() {
        if (!thumbtrack || !thumbsEl || !thumbs.length) return;
        var gap = 10;
        var step = thumbs[0].getBoundingClientRect().width + gap;
        if (step <= gap) return;
        var visible = Math.max(1, Math.round((thumbsEl.clientWidth + gap) / step));
        var maxStart = Math.max(0, n - visible);
        if (i < thumbStart) thumbStart = i;
        else if (i > thumbStart + visible - 1) thumbStart = i - visible + 1;
        if (thumbStart > maxStart) thumbStart = maxStart;
        if (thumbStart < 0) thumbStart = 0;
        thumbtrack.style.transform = 'translateX(' + (-thumbStart * step) + 'px)';
      }
      function show(k) {
        i = k < 0 ? 0 : (k > n - 1 ? n - 1 : k);
        track.style.transform = 'translateX(' + (-i * 100) + '%)';   // the slide effect
        thumbs.forEach(function (t, idx) { t.classList.toggle('brb_is_active', idx === i); });
        if (prev) prev.classList.toggle('brb_is_hidden', i === 0);
        if (next) next.classList.toggle('brb_is_hidden', i === n - 1);
        layoutThumbs();
      }
      thumbs.forEach(function (t, idx) { t.addEventListener('click', function () { show(idx); }); });
      if (prev) prev.addEventListener('click', function () { show(i - 1); });
      if (next) next.addEventListener('click', function () { show(i + 1); });
      if (viewport) {
        var sx = null;
        viewport.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
        viewport.addEventListener('touchend', function (e) {
          if (sx === null) return;
          var dx = e.changedTouches[0].clientX - sx;
          if (Math.abs(dx) > 40) show(i + (dx < 0 ? 1 : -1));
          sx = null;
        }, { passive: true });
      }
      show(0);
    });
  }

  /* ---- Curved wave dividers for .brb_sec--wave (white SVG top + bottom) ---- */
  function initWave(root) {
    var TOP = '<svg viewBox="0 0 1599 49" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><path fill="#ffffff" d="M1600 48.522L1533.33 45.8263C1466.67 43.1307 1333.33 37.7393 1200 36.8408C1066.67 35.9422 933.333 39.5364 800 43.1307C666.667 46.7249 533.333 50.3191 400 48.522C266.667 46.7249 133.333 39.5364 66.6666 35.9422L0 32.348V3.8147e-06H66.6666C133.333 3.8147e-06 266.667 3.8147e-06 400 3.8147e-06C533.333 3.8147e-06 666.667 3.8147e-06 800 3.8147e-06C933.333 3.8147e-06 1066.67 3.8147e-06 1200 3.8147e-06C1333.33 3.8147e-06 1466.67 3.8147e-06 1533.33 3.8147e-06H1600V48.522Z"/></svg>';
    var BOT = '<svg viewBox="0 0 1600 49" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><path fill="#ffffff" d="M1600 0.478007L1533.33 3.17367C1466.67 5.86934 1333.33 11.2607 1200 12.1592C1066.67 13.0578 933.333 9.46356 800 5.86934C666.667 2.27512 533.333 -1.3191 400 0.478007C266.667 2.27512 133.333 9.46356 66.6666 13.0578L0 16.652V49H66.6666C133.333 49 266.667 49 400 49C533.333 49 666.667 49 800 49C933.333 49 1066.67 49 1200 49C1333.33 49 1466.67 49 1533.33 49H1600V0.478007Z"/></svg>';
    findAll(root, '.brb_sec--wave').forEach(function (s) {
      if (s.__nhWave) return;
      s.__nhWave = true;
      var t = document.createElement('span'); t.className = 'brb_wave brb_wave--top'; t.setAttribute('aria-hidden', 'true'); t.innerHTML = TOP;
      var b = document.createElement('span'); b.className = 'brb_wave brb_wave--bottom'; b.setAttribute('aria-hidden', 'true'); b.innerHTML = BOT;
      s.insertBefore(t, s.firstChild);
      s.appendChild(b);
    });
  }

  /* ---- Inline video play — NO native controls; the custom play button is the
     only visible control. Tap plays; tap again pauses (play button reappears). ---- */
  function initVideoPlay(root) {
    findAll(root, '[data-video-play]').forEach(function (item) {
      if (item.__nhVid) return;
      item.__nhVid = true;
      var video = item.querySelector('video');
      var btn = item.querySelector('.brb_vrev__play');
      if (!video) return;
      video.removeAttribute('controls');   // never show native controls
      video.controls = false;
      function toggle() {
        if (video.paused || video.ended) {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          video.pause();
        }
      }
      if (btn) btn.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
      video.addEventListener('click', toggle);
      // brb_is_playing hides the play button (CSS); removing it brings the play button back
      video.addEventListener('play', function () { video.controls = false; item.classList.add('brb_is_playing'); });
      video.addEventListener('pause', function () { item.classList.remove('brb_is_playing'); });
      video.addEventListener('ended', function () { item.classList.remove('brb_is_playing'); });
    });
  }

  /* ---- Sticky CTA reveal after scrolling past hero ---- */
  function initSticky() {
    var bars = document.querySelectorAll('[data-sticky-cta]');
    if (!bars.length) return;
    bars.forEach(function (b) { b.style.transform = 'translateY(110%)'; b.style.transition = 'transform .3s ease'; });
    function onScroll() {
      var show = window.scrollY > 600;
      bars.forEach(function (b) { b.style.transform = show ? 'translateY(0)' : 'translateY(110%)'; });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---- Smooth scroll for in-page CTA anchors ---- */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ---- Interactive 1/2/3-bottle offer selector (sections/brb_offer.liquid) ----
     Adds the selected offer to the current cart without removing existing lines,
     then hands off to checkout after Shopify confirms the add. */
  function initOffer(scope) {
    findAll(scope, '[data-brb_offer]').forEach(function (root) {
      if (root.__nhOfferBound) return;
      root.__nhOfferBound = true;

      var sym = root.getAttribute('data-currency') || '$';
      var ctaTpl = root.getAttribute('data-cta-label') || 'ADD TO CART';
      var cards = Array.prototype.slice.call(root.querySelectorAll('[data-option]'));
      var radios = Array.prototype.slice.call(root.querySelectorAll('.brb_osel__radio'));
      var cta = root.querySelector('[data-offer-cta]');
      var ctaLabel = cta ? cta.querySelector('[data-cta-label-text]') : null;
      var ctaMain = cta ? cta.querySelector('[data-cta-main]') : null;
      var ctaSep = cta ? cta.querySelector('[data-cta-sep]') : null;
      var ctaOffer = cta ? cta.querySelector('[data-cta-offer]') : null;
      var msg = root.querySelector('[data-offer-msg]');
      var amtEl = root.querySelector('[data-total-amt]');
      var saveEl = root.querySelector('[data-total-save]');
      var saveWrap = root.querySelector('[data-total-save-wrap]');
      var purchaseRows = Array.prototype.slice.call(root.querySelectorAll('[data-purchase-row]'));
      var purchaseRadios = Array.prototype.slice.call(root.querySelectorAll('.brb_hbuy__radio'));
      var onetimePrice = root.querySelector('[data-onetime-price]');
      var onetimeCompare = root.querySelector('.brb_hbuy__prices [data-onetime-compare]');
      var subscribePrice = root.querySelector('[data-subscribe-price]');
      var row = root.closest ? root.closest('.brb_of2__row') : null;
      var imageEl = row ? row.querySelector('[data-offer-image]') : null;
      var routesRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      if (routesRoot.slice(-1) !== '/') routesRoot += '/';

      function money(n) {
        var v = (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);
        return sym + v;
      }

      function setCtaText(text) {
        var label = String(text || '').trim();
        if (ctaMain && ctaOffer) {
          var parts = label.match(/^(.*?)\s+([-\u2013\u2014])\s+(.*)$/);
          var main = parts ? parts[1] : label;
          var sep = parts && parts[2] === '\u2013' ? '\u2013' : '\u2014';
          var offer = parts ? parts[3] : '';
          ctaMain.textContent = main;
          ctaOffer.textContent = offer;
          ctaOffer.hidden = !offer;
          if (ctaSep) {
            ctaSep.textContent = sep;
            ctaSep.hidden = !offer;
          }
        } else if (ctaLabel) {
          ctaLabel.textContent = label;
        } else if (cta) {
          cta.textContent = label;
        }
      }

      function selectedCard() {
        var checked = root.querySelector('.brb_osel__radio:checked');
        if (!checked) return cards[0];
        return cards.filter(function (c) {
          var r = c.querySelector('.brb_osel__radio');
          return r && r.value === checked.value;
        })[0] || cards[0];
      }

      function purchaseMode() {
        var checked = root.querySelector('.brb_hbuy__radio:checked');
        return checked && checked.value === 'subscribe' ? 'subscribe' : 'onetime';
      }

      function bindSelectionButtons(items, selector) {
        items.forEach(function (item, index) {
          var radio = item.querySelector(selector);
          if (!radio) return;
          radio.hidden = true;
          item.tabIndex = 0;
          item.setAttribute('role', 'button');
          function select() {
            if (radio.disabled) return;
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              render();
            }
          }
          item.addEventListener('click', function (event) {
            if (event.target !== radio) select();
          });
          item.addEventListener('keydown', function (event) {
            var targetIndex = index;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index + items.length - 1) % items.length;
            else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % items.length;
            else if (event.key === 'Home') targetIndex = 0;
            else if (event.key === 'End') targetIndex = items.length - 1;
            else if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            var target = items[targetIndex];
            if (!target) return;
            target.focus();
            var targetRadio = target.querySelector(selector);
            if (targetRadio && !targetRadio.disabled) {
              targetRadio.checked = true;
              targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      }

      function render() {
        var c = selectedCard();
        if (!c) return;
        var mode = purchaseMode();
        cards.forEach(function (x) {
          var selected = x === c;
          x.classList.toggle('brb_is_selected', selected);
          x.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        purchaseRows.forEach(function (x) {
          var selected = x.getAttribute('data-mode') === mode;
          x.classList.toggle('brb_hbuy__row--active', selected);
          x.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        cards.forEach(function (x) {
          var now = x.querySelector('.brb_osel__now');
          var was = x.querySelector('.brb_osel__was');
          var suffix = x.getAttribute('data-unit-suffix') || '';
          var onetimeUnit = parseFloat(x.getAttribute('data-onetime-unit') || '');
          var subscribeUnit = parseFloat(x.getAttribute('data-subscribe-unit') || '');
          var compareUnit = parseFloat(x.getAttribute('data-compare-unit') || '');
          var comparePolicy = x.getAttribute('data-show-compare');
          var allowCompare = comparePolicy === null ? true : comparePolicy === 'true';
          var activeUnit = mode === 'subscribe' && isFinite(subscribeUnit) ? subscribeUnit : onetimeUnit;
          if (now && isFinite(activeUnit)) {
            now.textContent = money(activeUnit);
            if (suffix) {
              var small = document.createElement('small');
              small.textContent = suffix;
              now.appendChild(document.createTextNode(' '));
              now.appendChild(small);
            }
          }
          if (was) {
            var shouldShow = mode === 'subscribe' && isFinite(onetimeUnit) && onetimeUnit > activeUnit;
            var wasValue = shouldShow ? onetimeUnit : compareUnit;
            var showWas = allowCompare && isFinite(wasValue) && wasValue > activeUnit;
            was.hidden = !showWas;
            if (showWas) was.textContent = money(wasValue);
          }
        });

        var total = parseFloat(c.getAttribute(mode === 'subscribe' ? 'data-subscribe-total' : 'data-onetime-total') || c.getAttribute('data-total'));
        var save = parseFloat(c.getAttribute(mode === 'subscribe' ? 'data-subscribe-save' : 'data-onetime-save') || c.getAttribute('data-save'));
        var pct = c.getAttribute('data-pct') || '';
        var img = c.getAttribute('data-image') || '';
        var imgAlt = c.getAttribute('data-image-alt') || '';

        var oneTimeTotal = parseFloat(c.getAttribute('data-onetime-total') || c.getAttribute('data-total'));
        var oneTimeCompare = parseFloat(c.getAttribute('data-onetime-compare') || '');
        var subscribeTotal = parseFloat(c.getAttribute('data-subscribe-total') || c.getAttribute('data-total'));
        if (amtEl) amtEl.textContent = isFinite(total) ? money(total) : money(0);
        if (onetimePrice && isFinite(oneTimeTotal)) onetimePrice.textContent = money(oneTimeTotal);
        if (onetimeCompare) {
          var showCompare = isFinite(oneTimeCompare) && isFinite(oneTimeTotal) && oneTimeCompare > oneTimeTotal;
          onetimeCompare.hidden = !showCompare;
          if (showCompare) onetimeCompare.textContent = money(oneTimeCompare);
        }
        if (subscribePrice && isFinite(subscribeTotal)) subscribePrice.textContent = money(subscribeTotal);
        if (saveWrap) {
          var hasSave = isFinite(save) && save > 0;
          saveWrap.style.display = hasSave ? '' : 'none';
          if (hasSave && saveEl) saveEl.textContent = money(save);
        }
        if (cta) {
          var pctValue = parseFloat(pct);
          var ctaText = ctaTpl.indexOf('{pct}') !== -1
            ? ctaTpl.replace('{pct}', (pctValue > 0 ? pct + '%' : '50%'))
            : ctaTpl;
          setCtaText(ctaText);
        }
        if (imageEl && img && imageEl.getAttribute('src') !== img) {
          imageEl.setAttribute('src', img);
          if (imgAlt) imageEl.setAttribute('alt', imgAlt);
        }
      }

      [radios, purchaseRadios].forEach(function (group) {
        if (group.some(function (radio) { return radio.checked; })) return;
        var first = group.filter(function (radio) { return !radio.disabled; })[0];
        if (first) first.checked = true;
      });
      bindSelectionButtons(cards, '.brb_osel__radio');
      bindSelectionButtons(purchaseRows, '.brb_hbuy__radio');
      radios.forEach(function (r) { r.addEventListener('change', render); });
      purchaseRadios.forEach(function (r) { r.addEventListener('change', render); });

      if (cta) {
        cta.addEventListener('click', function (e) {
          e.preventDefault();
          var c = selectedCard();
          var variantId = c ? c.getAttribute('data-variant') : '';
          if (!variantId) {
            if (msg) { msg.textContent = 'This offer is not yet linked to a product. Pick a product in the theme editor.'; msg.className = 'brb_osel__msg brb_is_error'; }
            return;
          }
          var qty = parseInt(c.getAttribute('data-qty'), 10) || 1;
          var mode = purchaseMode();
          var line = { id: Number(variantId), quantity: qty };
          if (mode === 'subscribe') {
            var sellingPlan = c.getAttribute('data-selling-plan') || '';
            if (!sellingPlan) {
              if (msg) { msg.textContent = 'Subscribe & Save needs a selling plan on this product before checkout can apply the subscription discount.'; msg.className = 'brb_osel__msg brb_is_error'; }
              return;
            }
            line.selling_plan = Number(sellingPlan);
            line.properties = {
              '_purchase_type': 'Subscribe & Save',
              '_subscribe_discount_percent': c.getAttribute('data-subscribe-discount') || ''
            };
          }

          cta.disabled = true;
          cta.style.opacity = '0.7';
          if (msg) { msg.textContent = 'Adding to cart…'; msg.className = 'brb_osel__msg'; }

          function fail(text) {
            cta.disabled = false;
            cta.style.opacity = '';
            if (msg) { msg.textContent = text; msg.className = 'brb_osel__msg brb_is_error'; }
          }

          function parseCartResponse(r) {
            return r.json().catch(function () { return {}; }).then(function (j) {
              return { ok: r.ok, j: j };
            });
          }

          fetch(routesRoot + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [line] })
          })
            .then(parseCartResponse)
            .then(function (res) {
              if (!res.ok) {
                fail((res.j && res.j.description) ? res.j.description : 'Could not add to cart. Please try again.');
                return;
              }
              (window.NutraFunnelCheckout || {
                redirectAfterCartAdd: function (root) {
                  window.location.assign((root || '/') + 'checkout');
                }
              }).redirectAfterCartAdd(routesRoot);
            })
            .catch(function (err) {
              fail('Could not start checkout: ' + err.message);
            });
        });
      }

      render();
    });
  }

  function initReviewLoadMore(root) {
    findAll(root, '[data-brb-load-more]').forEach(function (btn) {
      if (btn.__nhLoadMoreBound) return;
      btn.__nhLoadMoreBound = true;
      btn.addEventListener('click', function () {
        var wrap = btn.closest('.brb_rev__wrap');
        var lists = wrap ? findAll(wrap, '[data-brb-load-more-list]') : [];
        if (!lists.length) return;
        lists.forEach(function (list) { list.classList.add('brb_is_expanded'); });
        var load = btn.closest('.brb_rev__load');
        if (load) load.hidden = true;
      });
    });
  }

  function initButtonSweep(root) {
    findAll(root, '.brb_btn, .brb_intro__btn').forEach(function (btn) {
      if (btn.__brbSweepBound) return;
      btn.__brbSweepBound = true;
      btn.classList.add('brb_sweep-target');

      function playSweep() {
        btn.classList.remove('brb_is_sweeping');
        void btn.offsetWidth;
        btn.classList.add('brb_is_sweeping');
      }

      btn.addEventListener('pointerenter', playSweep);
      btn.addEventListener('pointerleave', playSweep);
      btn.addEventListener('animationend', function (event) {
        if (event.animationName === 'brb_button_light_sweep') {
          btn.classList.remove('brb_is_sweeping');
        }
      });
    });
  }

  function initHeaderMenu(root) {
    findAll(root, '[data-brb-menu-toggle]').forEach(function (btn) {
      if (btn.__brbMenuBound) return;
      btn.__brbMenuBound = true;
      var header = btn.closest('.brb_header');
      var drawerId = btn.getAttribute('aria-controls');
      var drawer = drawerId ? document.getElementById(drawerId) : null;
      function setOpen(open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (header) header.classList.toggle('brb_menu_open', open);
      }
      btn.addEventListener('click', function () {
        setOpen(btn.getAttribute('aria-expanded') !== 'true');
      });
      if (drawer) {
        drawer.addEventListener('click', function (event) {
          if (event.target && event.target.closest('a')) setOpen(false);
        });
      }
      document.addEventListener('click', function (event) {
        if (!header || header.contains(event.target)) return;
        setOpen(false);
      });
    });
  }

  function handleBlockSelect(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var faqItem = target.closest('.brb_faq__item');
    if (faqItem) {
      var group = faqItem.closest('.brb_faq');
      if (group && !group.classList.contains('brb_faq--multi')) {
        group.querySelectorAll('.brb_faq__item.brb_is_open').forEach(function (item) {
          if (item !== faqItem) setFaqOpen(item, false);
        });
      }
      setFaqOpen(faqItem, true);
    }

    var slide = target.closest('.swiper-slide');
    if (slide) {
      var swiperEl = slide.closest('.brb_swiper');
      if (swiperEl && swiperEl.__swiper) {
        var slides = Array.prototype.slice.call(swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide'));
        var index = slides.indexOf(slide);
        if (index > -1) {
          var realIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
          if (swiperEl.__swiper.params && swiperEl.__swiper.params.loop && !isNaN(realIndex) && swiperEl.__swiper.slideToLoop) {
            swiperEl.__swiper.slideToLoop(realIndex);
          } else {
            swiperEl.__swiper.slideTo(index);
          }
        }
      }
    }
  }

  function boot(root) {
    var scope = root && root.querySelectorAll ? root : document;
    initAccordion(scope);       // safe to re-run (per-element guard)
    initSwipers(scope);         // safe to re-run (per-element guard)
    initHofGallery(scope);      // safe to re-run (per-element guard)
    initVideoPlay(scope);       // safe to re-run (per-element guard)
    initWave(scope);            // safe to re-run (per-element guard)
    initOffer(scope);           // safe to re-run (per-element guard)
    initReviewLoadMore(scope);  // safe to re-run (per-element guard)
    initButtonSweep(scope);     // safe to re-run (per-element guard)
    initHeaderMenu(scope);      // safe to re-run (per-element guard)
    if (!window.__nhBootOnce) {
      window.__nhBootOnce = true;
      initSticky();
      initSmoothScroll();      // document-level delegation — bind once only
    }
    setTimeout(function () {
      initAccordion(scope);
      initSwipers(scope);
    }, 800); // re-bind after theme-editor DOM updates
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  if (!window.__nhFunnelSectionLoadBound) {
    window.__nhFunnelSectionLoadBound = true;
    document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
    document.addEventListener('shopify:section:unload', function (event) { destroySwipers(event.target); });
    document.addEventListener('shopify:block:select', handleBlockSelect);
  }
})();
